import React, { createContext, useCallback, useMemo, useReducer, useState } from 'react'
import ReactDOM from 'react-dom'
import isHotkey from 'is-hotkey'
import { Editable, withReact, Slate } from 'slate-react'
import { createEditor } from 'slate'
import { withHistory } from 'slate-history'
import SlateToolbar from './toolbar'
import { toggleMark } from './toolbar/mark-button'
import SlateElement from './element'
import SlateLeaf from './leaf'
import withTables from './plugins/table'
import { useMention, withMentions } from './plugins/mention'
import SlateEditorReducer, { initSlateEditorState } from './slate-editor-reducer'
import './index.less'
import classNames from 'classnames'
import { withImage } from './plugins/withImage'

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code'
}

const initialValue = [
  {
    type: 'paragraph',
    children: [{ text: '' }]
  }
]

export const SlateEditorContext = createContext({})

export const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body)
}

const plugins = [withReact, withHistory, withTables, withMentions, withImage]

const OSlateEditor = (props: any) => {
  const [state, dispatch] = useReducer(SlateEditorReducer, initSlateEditorState)
  let defaultVal = initialValue
  if (props.value) {
    const propsVal = JSON.parse(props.value)
    defaultVal = Array.isArray(propsVal) ? propsVal : initialValue
  }
  const [value, setValue] = useState(defaultVal)

  const renderElement = useCallback((elemProps) => <SlateElement {...elemProps} />, [])
  const renderLeaf = useCallback((leafProps) => <SlateLeaf {...leafProps} />, [])
  const editor = useMemo(() => {
    let oEditor = createEditor()
    plugins.forEach((plugin) => {
      oEditor = plugin(oEditor)
    })

    return oEditor
  }, [])

  const mention = useMention(editor)

  const onChange = (val: any) => {
    if (props.onChange) {
      props.onChange(JSON.stringify(val))
    }
  }

  const onKeyDown = useCallback(
    (event) => {
      mention.onKeyDown(event)
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault()
          const mark = HOTKEYS[hotkey]
          toggleMark(editor, mark)
        }
      }
    },
    [mention]
  )

  return (
    <SlateEditorContext.Provider value={{ state, dispatch }}>
      <Slate
        editor={editor as any}
        initialValue={value}
        onChange={(nValue: any) => {
          if (value === nValue) {
            return
          }
          // @ts-ignore
          mention.onChange(nValue)
          setValue(nValue)
          onChange(nValue)
        }}
      >
        {!props.readOnly && <SlateToolbar />}
        <Editable
          className={classNames('slate-editor-body', { readonly: props.readOnly || false })}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          readOnly={props.readOnly || false}
          onKeyDown={onKeyDown}
        />
        {mention.ui}
      </Slate>
    </SlateEditorContext.Provider>
  )
}

export default OSlateEditor
